<template>
  <div class="card m-3 text-center">
    <h4 class="card-header">{{ t('set-new-password') }}</h4>
    <div class="d-flex card-body justify-content-center">
      <Form
        @submit="onSubmit"
        :validation-schema="schema"
        v-slot="{ errors, isSubmitting }"
      >
        <!-- Pole na token -->
        <div class="form-group row mb-2">
          <label class="col-sm-4 col-form-label">{{ t('token') }}</label>
          <div class="col-sm-8">
            <Field
              name="token"
              type="text"
              class="form-control"
              :class="{ 'is-invalid': errors.token }"
            />
            <div class="invalid-feedback">{{ errors.token }}</div>
          </div>
        </div>

        <!-- Nowe hasło -->
        <div class="form-group row mb-2">
          <label class="col-sm-4 col-form-label">{{ t('new-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="newPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.newPassword }"
            />
            <div class="invalid-feedback">{{ errors.newPassword }}</div>
          </div>
        </div>

        <!-- Powtórzenie nowego hasła -->
        <div class="form-group row mb-3">
          <label class="col-sm-4 col-form-label">{{ t('confirm-password') }}</label>
          <div class="col-sm-8">
            <Field
              name="confirmPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': errors.confirmPassword }"
            />
            <div class="invalid-feedback">{{ errors.confirmPassword }}</div>
          </div>
        </div>

        <!-- Przycisk i komunikaty -->
        <div class="form-group d-flex flex-column align-items-center">
          <div class="col-auto">
            <button
              class="btn btn-outline-secondary mt-2"
              :disabled="isSubmitting"
            >
              <span
                v-show="isSubmitting"
                class="spinner-border spinner-border-sm mr-1"
              ></span>
              {{ t('change-password') }}
            </button>
          </div>
          <!-- Komunikat o sukcesie -->
          <div v-if="success" class="text-success mt-3">{{ t('password-changed') }}</div>
          <!-- Komunikat o błędzie -->
          <div v-if="error" class="text-danger mt-3">{{ error }}</div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script setup>
// Importy potrzebnych bibliotek i narzędzi
import { ref, computed } from "vue";
import { Form, Field } from "vee-validate";
import * as yup from "yup";
import { useAuthStore } from "@/stores/auth.store";
import { useRoute } from "vue-router";
import router from "@/router";
import { useI18n } from 'vue-i18n';

// Pobieranie emaila z parametrów URL (np. /reset-password/reset/:email)
const route = useRoute();
const email = computed(() => route.query.email || route.params.email);

// Reaktywne zmienne na sukces/błąd
const success = ref(false);
const error = ref(null);
const { t } = useI18n();

// Walidacja formularza
const schema = yup.object({
  token: yup.string().required(t('token-required')),
  newPassword: yup
    .string()
    .required(t('user-password-required'))
    .min(6, t('password-must-be-at-least-6')),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("newPassword")], t('passwords-must-be-the-same'))
    .required(t('password-confirmation-required')),
});

// Logika wysyłki formularza
const onSubmit = async (values) => {
  error.value = null;
  success.value = false;

  try {
    const authStore = useAuthStore();

    // Wywołanie akcji zmiany hasła z przekazaniem emaila, nowego hasła i tokena
    const response = await authStore.setPassword(
      email.value,
      values.newPassword,
      values.token
    );

    // Jeśli odpowiedź zawiera błąd, pokazujemy go użytkownikowi
    if(response !== null) {
      error.value = response.split(":").slice(1).join(":").trim();
      return;
    }

    // Sukces – przekierowanie na stronę logowania
    success.value = true;
    router.push("/login");
  } catch (err) {
    error.value = err.message;
    console.error("Błąd HTTP:", err);
  }
};
</script>

<style scoped>
.card-body {
  background: #f5f4f3;
}
.card-body form {
  width: 400px;
}
.col-form-label {
  text-align: right;
  line-height: 25px;
}
.form-group {
  font-size: 12px;
}
button {
  border: 0;
  color: #231f20;
  font-size: 15px;
  font-weight: bold;
}
a {
  color: #231f20;
  font-size: 12px;
  text-decoration: none;
}
input {
  border: 0;
  border-left: 1px solid #c8c7c7;
  border-top: 1px solid #c8c7c7;
  padding: 10px;
}
input:focus {
  border-color: #c8c7c7;
  box-shadow: 0 0 0 0.25rem rgba(100, 101, 104, 0.25);
}
</style>
